import fetchAboutCandidates from '@Application/Subjects/fetchAboutCandidates';
import FetchAboutCandidates from '@Application/Subjects/Queries/FetchAboutCandidates';
import { theme } from '@Guidelines/Theme/theme';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import AboutCandidateView from '@UI/Store/Subjects/Views/AboutCandidateView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Autocomplete } from '@verclam/ui-kit';

const SEARCH_DELAY_IN_MILLISECONDS = 300;
const SEARCH_MINIMUM_LENGTH = 2;

const candidateNameStyle = css`
	display: block;
`;

const candidateDetailStyle = css`
	display: block;
	font-family: ${theme.fonts.mono};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly label: string;
	readonly facilityId: number;
	readonly typeId: number;
	readonly allowsExternal: boolean;
	readonly value: AboutCandidateView | null;
	readonly onChange: (candidate: AboutCandidateView | null) => void;
	readonly errorMessage: string | undefined;
	readonly isFormFieldValid: boolean;
};

const describeCandidate = (candidate: AboutCandidateView): string =>
	candidate.detail === null ? candidate.kind.label : `${candidate.detail} · ${candidate.kind.label}`;

/**
 * Searches the people and facilities a dossier can be about. The search is debounced by the field
 * itself; only the answer of the latest search is kept.
 */
const AboutField: React.FC<Props> = ({
	label,
	facilityId,
	typeId,
	allowsExternal,
	value,
	onChange,
	errorMessage,
	isFormFieldValid,
}) => {
	const dispatch = useDispatch();
	const [suggestions, setSuggestions] = React.useState<readonly AboutCandidateView[]>([]);
	const [isSearching, setIsSearching] = React.useState(false);
	const latestSearch = React.useRef(0);

	const handleSearch = React.useCallback(
		(query: string) => {
			latestSearch.current += 1;
			const search = latestSearch.current;
			setIsSearching(true);

			dispatch(fetchAboutCandidates(new FetchAboutCandidates(facilityId, typeId, query)))
				.then((action) => {
					if (search === latestSearch.current && action.type === 'ABOUT_CANDIDATES_FETCHED') {
						setSuggestions(
							action.payload.candidates.map((candidate) => new AboutCandidateView(candidate)),
						);
					}
				})
				.catch(ignoreHandledError)
				.finally(() => {
					if (search === latestSearch.current) {
						setIsSearching(false);
					}
				});
		},
		[dispatch, facilityId, typeId],
	);

	const handleChange = React.useCallback(
		(selection: AboutCandidateView | readonly AboutCandidateView[] | null) => {
			onChange(
				selection === null || Array.isArray(selection) ? null : (selection as AboutCandidateView),
			);
		},
		[onChange],
	);

	const renderCandidate = React.useCallback((option: unknown) => {
		const candidate = option as AboutCandidateView;

		return (
			<React.Fragment>
				<span css={candidateNameStyle}>{candidate.name}</span>
				<span css={candidateDetailStyle}>{describeCandidate(candidate)}</span>
			</React.Fragment>
		);
	}, []);

	return (
		<Autocomplete<AboutCandidateView>
			name="about"
			label={label}
			subLabel={
				allowsExternal
					? "Laissez vide pour une personne extérieure : son nom est l'intitulé."
					: undefined
			}
			required={!allowsExternal}
			field="name"
			value={value}
			onChange={handleChange}
			suggestions={suggestions}
			onSearch={handleSearch}
			delay={SEARCH_DELAY_IN_MILLISECONDS}
			minLength={SEARCH_MINIMUM_LENGTH}
			loading={isSearching}
			showEmptyMessage
			emptyMessage="Aucune personne trouvée"
			itemTemplate={renderCandidate}
			errorMessage={errorMessage}
			isFormFieldValid={isFormFieldValid}
		/>
	);
};

export default AboutField;
